import { Field } from "components/shared/form";
import { BidPaymentStatus } from "api";
import { dateToView } from "helpers/date";
import { settlementHistoryAmounts, settlementPaidPercent } from "helpers/mutualSettlements";
import { formatValue } from "helpers/reports";
import moment from "moment";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { applyPaymentOperation, deletePaymentOperation, getPaymentHistory } from "sources/books/settlementPayments";
import { PanelStatus } from "./sidePanel";
import PanelDatetime from "./panelDatetime";
import { BasisInfo, PaymentStatusBadge } from "./paymentDetails";
import WithChangeDate from "./withChangeDate";

/** Read and edit operations without replacing the current payment or existing report comment. */
export default function PaymentHistory(props) {
    const { row, isAccountsPayable, canEdit } = props;
    const [state, setState] = useState({
        history: null,
        loading: true,
        error: "",
        editing: null,
        saving: false,
        saveError: "",
    });
    const update = useCallback(
        (patch) =>
            setState((previous) => ({
                ...previous,
                ...(typeof patch === "function" ? patch(previous) : patch),
            })),
        [],
    );
    const requestIdRef = useRef(0);
    const pending = useRef(false);
    const mounted = useRef(false);
    const latest = useRef(null);
    const identity = JSON.stringify([row.bidId, !!row.isDistributionBid, !!isAccountsPayable]);
    const requestKey = JSON.stringify([identity, props.revision]);
    const previousIdentity = useRef(identity);
    useLayoutEffect(() => {
        latest.current = { props, state, identity, requestKey };
    });

    const load = useCallback(async () => {
        const current = latest.current;
        const requestId = ++requestIdRef.current;
        const isCurrent = () =>
            mounted.current && requestId === requestIdRef.current && current.requestKey === latest.current.requestKey;
        update({ loading: true, error: "" });
        try {
            const history = await getPaymentHistory(current.props.row, current.props.isAccountsPayable);
            if (!isCurrent()) {
                return;
            }
            if (!history) {
                throw new Error("Не удалось загрузить историю оплат.");
            }
            update({ history, loading: false });
        } catch (error) {
            if (isCurrent()) {
                update({ error: error.message, loading: false });
            }
        }
    }, [update]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            requestIdRef.current += 1;
        };
    }, []);
    useEffect(() => {
        if (previousIdentity.current !== identity) {
            previousIdentity.current = identity;
            update({ history: null, editing: null, saveError: "" });
        }
        load();
    }, [identity, load, requestKey, update]);

    const startEditing = (operation) =>
        update({
            editing: operation ? { ...operation } : { id: 0, amount: "", date: moment().format(), comment: "" },
            saveError: "",
        });
    const changeOperation = ({ name, value }) =>
        update((previous) => ({ editing: { ...previous.editing, [name]: value }, saveError: "" }));

    const refreshAfterMutation = async (current) => {
        const previousRequest = requestIdRef.current;
        // A completed mutation must refresh the report even if its sidebar has already closed.
        await current.props.refresh();
        if (
            mounted.current &&
            latest.current.identity === current.identity &&
            requestIdRef.current === previousRequest
        ) {
            await load();
        }
    };
    const save = async () => {
        const current = latest.current;
        if (pending.current || !current.props.canEdit || !current.state.editing) {
            return;
        }
        const { editing } = current.state;
        const isCurrent = () => mounted.current && latest.current.identity === current.identity;
        if (
            !Number.isFinite(Number(editing.amount)) ||
            Number(editing.amount) <= 0 ||
            !editing.date ||
            !moment(editing.date).isValid()
        ) {
            update({ saveError: "Укажите положительную сумму и дату операции." });
            return;
        }
        pending.current = true;
        update({ saving: true, saveError: "" });
        try {
            await applyPaymentOperation(current.props.row, current.props.isAccountsPayable, {
                id: editing.id,
                amount: Number(editing.amount),
                date: moment(editing.date).format(),
                comment: editing.comment ?? "",
            });
            if (isCurrent()) {
                update({ editing: null });
            }
            await refreshAfterMutation(current);
        } catch (error) {
            if (isCurrent()) {
                update({ saveError: error.message });
            }
        } finally {
            pending.current = false;
            if (mounted.current) {
                update({ saving: false });
            }
        }
    };

    const remove = async (operationId) => {
        const current = latest.current;
        if (pending.current || !current.props.canEdit || !window.confirm("Удалить платёжную операцию?")) {
            return;
        }
        const isCurrent = () => mounted.current && latest.current.identity === current.identity;
        pending.current = true;
        update({ saving: true, saveError: "" });
        try {
            await deletePaymentOperation(current.props.row, current.props.isAccountsPayable, operationId);
            await refreshAfterMutation(current);
        } catch (error) {
            if (isCurrent()) {
                update({ saveError: error.message });
            }
        } finally {
            pending.current = false;
            if (mounted.current) {
                update({ saving: false });
            }
        }
    };

    const { history, loading, error, editing, saving, saveError } = state;
    const amounts = history ? settlementHistoryAmounts(history) : {};
    const progress = history ? settlementPaidPercent(history.price, amounts.received) : null;
    const currentPayment = history ? { ...row, ...history, bidPaymentStatus: history.paymentStatus } : row;
    return (
        <>
            <BasisInfo row={row} />
            <div className="mt-3">
                <PanelStatus loading={loading} error={error} retry={load} />
            </div>
            {history && (
                <>
                    <div className="settlements__history-summary mt-3">
                        {[
                            ["Стоимость", history.price],
                            [isAccountsPayable ? "Оплачено" : "Получено", amounts.received],
                            ["Остаток", amounts.remaining],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <div className="small text-muted mb-1">{label}</div>
                                <strong>{value == null ? "—" : formatValue(value, "double")}</strong>
                            </div>
                        ))}
                    </div>
                    {progress !== null && (
                        <div className="d-flex align-items-center mt-3">
                            <div
                                className="settlements__payment-progress flex-grow-1"
                                role="progressbar"
                                aria-label={isAccountsPayable ? "Оплачено" : "Получено"}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={progress}
                            >
                                <div style={{ width: `${progress}%` }} />
                            </div>
                            <span className="small text-muted ml-2">{progress}%</span>
                        </div>
                    )}
                    <div className="d-flex flex-wrap align-items-center mt-3 settlements__history-status">
                        <PaymentStatusBadge
                            status={history.paymentStatus}
                            overdue={row.isPaymentOverdue}
                            isAccountsPayable={isAccountsPayable}
                        />
                        {history.overdueDays > 0 && (
                            <span
                                className={
                                    history.paymentStatus === BidPaymentStatus.Paid
                                        ? "badge badge-light"
                                        : "badge badge-danger"
                                }
                            >
                                {history.paymentStatus === BidPaymentStatus.Paid ? "Просрочка при оплате" : "Просрочка"}
                                : {history.overdueDays} дн.
                            </span>
                        )}
                        {history.planPaymentDate && (
                            <span className="small text-muted">
                                Плановая дата: {dateToView(history.planPaymentDate)}
                            </span>
                        )}
                    </div>
                    <section className="settlements__history-operations mt-4">
                        <div className="settlements__section-heading d-flex align-items-center justify-content-between">
                            <h6 className="mb-0">
                                Поступления{" "}
                                <span className="text-muted font-weight-normal ml-1">{history.operations.length}</span>
                            </h6>
                            {canEdit && !editing && (
                                <button
                                    type="button"
                                    className="btn btn-xs btn-brand"
                                    disabled={saving}
                                    onClick={() => startEditing()}
                                >
                                    Добавить
                                </button>
                            )}
                        </div>
                        {history.operations.length === 0 && (
                            <div className="settlements__empty">
                                <strong>Платёжных операций пока нет</strong>
                                {canEdit && (
                                    <p className="small text-muted mt-2 mb-0">
                                        Добавьте поступление, чтобы сохранить дату и сумму оплаты.
                                    </p>
                                )}
                            </div>
                        )}
                        {history.operations.map((operation) => (
                            <article key={operation.id} className="settlements__operation">
                                <div className="d-flex align-items-start justify-content-between">
                                    <div>
                                        <time dateTime={operation.date}>
                                            {moment(operation.date).format("DD.MM.YYYY")}
                                        </time>
                                        <span className="small text-muted ml-2">
                                            {moment(operation.date).format("HH:mm")}
                                        </span>
                                    </div>
                                    <strong className="ml-3">{formatValue(operation.amount, "double")}</strong>
                                </div>
                                <dl className="settlements__operation-details mb-0 mt-2">
                                    <div>
                                        <dt>Автор</dt>
                                        <dd>{operation.createdByName || "Не указан"}</dd>
                                    </div>
                                    <div>
                                        <dt>Комментарий</dt>
                                        <dd>{operation.comment || "—"}</dd>
                                    </div>
                                </dl>
                                {canEdit && (
                                    <div className="d-flex justify-content-end mt-2">
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-secondary mr-2"
                                            disabled={saving}
                                            onClick={() => startEditing(operation)}
                                        >
                                            Изменить
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-secondary"
                                            disabled={saving}
                                            onClick={() => remove(operation.id)}
                                        >
                                            Удалить
                                        </button>
                                    </div>
                                )}
                            </article>
                        ))}
                    </section>
                    {editing && (
                        <div className="settlements__operation-editor p-3 mt-3">
                            <h6 className="mb-2">{editing.id ? "Изменение операции" : "Новая операция"}</h6>
                            <Field
                                label="Сумма"
                                type="number"
                                bWrapper="form-group mb-2"
                                labelWrapper="d-block small text-muted mb-1"
                                fieldWrapper="w-100"
                                inputProps={{ name: "amount", step: "0.01", min: "0.01" }}
                                value={editing.amount}
                                onChange={changeOperation}
                                disabled={saving}
                            />
                            <PanelDatetime
                                label="Дата"
                                bWrapper="form-group mb-2"
                                labelWrapper="d-block small text-muted mb-1"
                                wrapper="w-100"
                                inputProps={{ name: "date", type: "datetime-local", disabled: saving }}
                                value={editing.date}
                                onChange={changeOperation}
                                disabled={saving}
                            />
                            <Field
                                textarea
                                label="Комментарий операции"
                                bWrapper="form-group mb-2"
                                labelWrapper="d-block small text-muted mb-1"
                                fieldWrapper="w-100"
                                inputProps={{ name: "comment", maxLength: 4096, rows: 2 }}
                                value={editing.comment}
                                onChange={changeOperation}
                                disabled={saving}
                            />
                            <div className="d-flex flex-wrap gap-1 mt-3">
                                <button type="button" className="btn btn-sm btn-brand" onClick={save} disabled={saving}>
                                    Сохранить
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => update({ editing: null })}
                                    disabled={saving}
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    )}
                    <h6 className="settlements__section-heading mt-4 mb-2">Комментарий к оплате</h6>
                    <WithChangeDate
                        name="comment"
                        commentPreview
                        payment={currentPayment}
                        isAccountsPayable={isAccountsPayable}
                        disabled={!canEdit || saving}
                        updateData={props.refresh}
                    />
                </>
            )}
            {saveError && (
                <div className="alert alert-danger mt-3" role="alert">
                    {saveError}
                </div>
            )}
        </>
    );
}
